import { interfaceError } from '../../i18n/errorMessages';
import { message as interfaceMessage } from '../../i18n/messages';
import LocalizedMessage from '../common/LocalizedMessage';
import React, {
  useEffect, useMemo, useRef, useState,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as bookActions from "../../redux/actions/bookActions";
import SettingsService from "../../services/SettingsService";
import LessonRegisterService from "../../services/LessonRegisterService";
import { ROLES } from "../common/Const";
import { DEFAULT_LESSON_TYPES_SETTING, getLessonTypesSetting, getLessonTypeById } from "../common/lessonTypes";
import { buildSchoolYearRange } from "../common/schoolYear";
import { canManageClassStudents, getEffectivePermissionRole } from "./rbac";
import { useTeacherAccess, scopeAllowsTeacher } from "./TeacherAccessProvider";
import {
  canEditLessonAttendance,
  canUndoWholeLessonCancellation,
  canUseAdministrativeLessonCommands,
  getAdministrativeLessonCommandBlockReason,
} from "./lessonCommandPermissions";
import { buildLessonRegisterCommandBasePayload } from "./lessonRegisterPayloads";
import {
  ATTENDANCE_DECISIONS,
  applyAttendanceDecision,
  buildAttendanceDraft,
  buildAttendanceSavePayload,
  getAttendanceSummary,
  markAllPresent,
  rebuildAttendanceDraftPreservingDecisions,
  validateAttendanceDraft,
} from "./attendanceDraft";
import { getAttendanceLessonViewModel } from "./attendanceLessonSelectors";
import AttendanceLessonHeader from "./AttendanceLessonHeader";
import AttendanceSummaryBar from "./AttendanceSummaryBar";
import AttendanceDecisionList from "./AttendanceDecisionList";
import AttendanceSaveBar from "./AttendanceSaveBar";
import useConfirmation from "../common/useConfirmation";
import AttendanceCommandModals from "./AttendanceCommandModals";
import LessonSummaryPanel from "./LessonSummaryPanel";
import ClassLessonHistoryModal from "./ClassLessonHistoryModal";
import { readDashboardState, writeDashboardState } from "./dashboardStateStorage";
import { useTranslation } from 'react-i18next';

const getLessonDashboardDate = (lessonDate) => {
  const parsedDate = new Date(`${lessonDate}T12:00:00`);

  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

export function AttendancePageBase({
  match,
  history,
  schoolClasses,
  teachers,
  students,
  classStudents,
  lessonRegister,
  books,
  activeSchoolYearStartYear,
  userRole,
  userTeacherId,
  userId,
  schoolId,
  activeContextKey,
  loadSchoolClasses,
  loadTeachers,
  loadStudents,
  loadClassStudents,
  loadLessonRegisters,
  loadBooks,
  saveLessonRegister,
  reportLessonAbsence,
  cancelWholeLesson,
  undoWholeLessonCancellation,
  addClassStudent,
  removeClassStudent,
}) {
  const { t: tr } = useTranslation();
  const dashboardScope = { accountId: userId, schoolId, contextKey: activeContextKey };
  const { classId, lessonIndex } = match.params;
  const { extensions } = useTeacherAccess();
  const assignedClass = schoolClasses.find(item => item.id === classId);
  const canManageStudents = canManageClassStudents(userRole) || (userRole === ROLES.TEACHER &&
    scopeAllowsTeacher(extensions.classesManageScope, userTeacherId, assignedClass && assignedClass.teacherId));
  const [draft, setDraft] = useState(null);
  const [attendanceDirty, setDirty] = useState(false);
  const [savedTopic, setSavedTopic] = useState("");
  const [savingTopic, setSavingTopic] = useState(false);
  const [topicSaved, setTopicSaved] = useState(false);
  const topicDirty = !!draft && (draft.lessonDetails.lessonSummary || "") !== savedTopic;
  const dirty = attendanceDirty || topicDirty;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [activeAction, setActiveAction] = useState("");
  const [draftKey, setDraftKey] = useState("");
  const [draftParticipantsKey, setDraftParticipantsKey] = useState("");
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [pendingCancelledPresence, setPendingCancelledPresence] = useState(null);
  const { confirm, confirmation } = useConfirmation();
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);
  const [historyModal, setHistoryModal] = useState({
    open: false, loading: false, error: "", items: [], nextCursor: null,
  });
  const unblockNavigationRef = useRef(null);
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);

  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
    if (teachers.length === 0) loadTeachers();
    if (students.length === 0) loadStudents();
    if (books.length === 0) loadBooks();
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    books.length,
    students.length,
    teachers.length,
    loadBooks,
    loadClassStudents,
    loadLessonRegisters,
    loadSchoolClasses,
    loadStudents,
    loadTeachers,
  ]);

  useEffect(() => {
    SettingsService.getSettings()
      .then(settings => setLessonTypes(getLessonTypesSetting(settings)))
      .catch(() => setLessonTypes(DEFAULT_LESSON_TYPES_SETTING));
  }, []);

  const viewModel = useMemo(() => getAttendanceLessonViewModel({
    schoolClasses,
    teachers,
    students,
    classStudents,
    lessonRegister,
    books,
    classId,
    lessonIndex,
    lessonTypes,
  }), [schoolClasses, teachers, students, classStudents, lessonRegister, books, classId, lessonIndex, lessonTypes]);

  const nextDraftKey = viewModel.status === "ready"
    ? [
      classId,
      lessonIndex,
      (viewModel.scheduledLesson.lessonRegister && viewModel.scheduledLesson.lessonRegister.id) || "",
      viewModel.lessonDetails.attendanceStatus || "",
      viewModel.lessonDetails.lessonStatus || "",
    ].join(":")
    : "";
  const nextDraftParticipantsKey = viewModel.status === "ready"
    ? viewModel.participants.map(participant => participant.id).sort().join(":")
    : "";

  useEffect(() => {
    if (!savingTopic && viewModel.status === "ready" && (
      !dirty ||
      nextDraftKey !== draftKey ||
      nextDraftParticipantsKey !== draftParticipantsKey
    )) {
      const identityChanged = nextDraftKey !== draftKey;
      const participantsChanged = nextDraftParticipantsKey !== draftParticipantsKey;

      setDraft(currentDraft => (
        dirty && participantsChanged && !identityChanged
          ? rebuildAttendanceDraftPreservingDecisions(currentDraft, viewModel.scheduledLesson)
          : buildAttendanceDraft(viewModel.scheduledLesson)
      ));
      setDirty(attendanceDirty && participantsChanged && !identityChanged);
      if (!dirty || identityChanged) {
        setSavedTopic(viewModel.lessonDetails.lessonSummary || "");
      }
      setError("");
      setValidationAttempted(false);
      setDraftKey(nextDraftKey);
      setDraftParticipantsKey(nextDraftParticipantsKey);
    }
  }, [
    savingTopic,
    attendanceDirty,
    dirty,
    draftKey,
    draftParticipantsKey,
    nextDraftKey,
    nextDraftParticipantsKey,
    viewModel.status,
    viewModel.scheduledLesson,
  ]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty || !history.block) {
      return undefined;
    }

    const unblockNavigation = history.block((nextLocation, action) => {
      const currentLocation = history.location || {};
      const isSameLocation = nextLocation.pathname === currentLocation.pathname &&
        nextLocation.search === currentLocation.search &&
        nextLocation.hash === currentLocation.hash;

      if (isSameLocation) {
        return true;
      }

      setPendingNavigation({ action, location: nextLocation });
      return false;
    });
    unblockNavigationRef.current = unblockNavigation;

    return () => {
      unblockNavigation();
      if (unblockNavigationRef.current === unblockNavigation) {
        unblockNavigationRef.current = null;
      }
    };
  }, [dirty, history]);

  if (viewModel.status !== "ready") {
    return (
      <div className="attendance-page" role="alert">{tr("manage:noClassesFound_48")}</div>
    );
  }

  if (!draft) {
    return <div className="attendance-page">{tr("manage:loadingAttendance_49")}</div>;
  }

  const canEdit = canEditLessonAttendance(userRole, userTeacherId, viewModel.lessonDetails) &&
    viewModel.lessonDetails.lessonStatus !== "cancelled";
  const canUseAdministrativeCommands = canUseAdministrativeLessonCommands({
    role: userRole,
    lesson: viewModel.scheduledLesson,
    lessonDetails: viewModel.lessonDetails,
  });
  const administrativeCommandBlockReason = getAdministrativeLessonCommandBlockReason({
    role: userRole,
    lesson: viewModel.scheduledLesson,
    lessonDetails: viewModel.lessonDetails,
  });
  const canUndoCancellation = canUndoWholeLessonCancellation({
    role: userRole,
    lesson: viewModel.scheduledLesson,
    lessonDetails: viewModel.lessonDetails,
  });
  const canEditLessonDetails = userRole !== ROLES.TEACHER;
  const canEditLessonComment = canEditLessonAttendance(userRole, userTeacherId, viewModel.lessonDetails) &&
    viewModel.lessonDetails.lessonStatus !== "cancelled";
  const summary = getAttendanceSummary(draft);
  const validation = validateAttendanceDraft(draft);
  const invalidStudentIds = validationAttempted ? validation.unmarkedStudentIds : [];

  const confirmNavigation = () => {
    const navigation = pendingNavigation;

    if (!navigation) {
      return;
    }

    setPendingNavigation(null);
    setDirty(false);
    setSavedTopic(draft.lessonDetails.lessonSummary || "");

    if (unblockNavigationRef.current) {
      unblockNavigationRef.current();
      unblockNavigationRef.current = null;
    }

    if (navigation.action === "REPLACE") {
      history.replace(navigation.location);
      return;
    }

    if (navigation.action === "POP" || !navigation.location) {
      history.goBack();
      return;
    }

    history.push(navigation.location);
  };

  const cancelNavigation = () => {
    setPendingNavigation(null);
  };

  const handleReturn = () => {
    if (dirty) {
      setPendingNavigation({ action: "POP", location: null });
      return;
    }

    history.goBack();
  };

  const handleDecisionChange = (studentId, decision) => {
    const currentDecision = draft.decisions[studentId];
    const isCancelledDecision = currentDecision && (
      currentDecision.decision === ATTENDANCE_DECISIONS.CANCELLED_ON_TIME ||
      currentDecision.decision === ATTENDANCE_DECISIONS.CANCELLED_LATE
    );

    if (isCancelledDecision && decision === ATTENDANCE_DECISIONS.PRESENT) {
      const student = draft.participants.find(participant => participant.id === studentId);
      setPendingCancelledPresence({
        studentId,
        studentName: student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "",
      });
      return;
    }

    setDraft(currentDraft => applyAttendanceDecision(currentDraft, studentId, decision));
    setDirty(true);
    setError("");
  };

  const confirmCancelledPresence = () => {
    const pendingPresence = pendingCancelledPresence;

    if (!pendingPresence) {
      return;
    }

    setDraft(currentDraft => applyAttendanceDecision(
      currentDraft,
      pendingPresence.studentId,
      ATTENDANCE_DECISIONS.PRESENT,
    ));
    setPendingCancelledPresence(null);
    setDirty(true);
    setError("");
  };

  const cancelCancelledPresence = () => {
    setPendingCancelledPresence(null);
  };

  const handleMarkAllPresent = () => {
    setDraft(currentDraft => markAllPresent(currentDraft));
    setDirty(true);
    setError("");
    setValidationAttempted(false);
  };

  const handleLessonSummaryChange = (lessonSummary) => {
    setDraft(currentDraft => ({
      ...currentDraft,
      lessonDetails: { ...currentDraft.lessonDetails, lessonSummary },
    }));
    setTopicSaved(false);
    setError("");
  };

  const handleSaveTopic = () => {
    if (!canEditLessonComment || !topicDirty || saving || savingTopic) return;
    const lesson = viewModel.scheduledLesson;
    const lessonSummary = draft.lessonDetails.lessonSummary || "";
    const payload = userRole === ROLES.TEACHER
      ? {
        ...(lesson.lessonRegister ? { id: lesson.lessonRegister.id } : {}),
        lessonId: lesson.id,
        lessonDetails: { lessonSummary },
      }
      : lesson.lessonRegister ? {
        ...lesson.lessonRegister,
        lessonDetails: { ...lesson.lessonRegister.lessonDetails, lessonSummary },
      } : buildLessonRegisterCommandBasePayload({
        lesson,
        lessonDetailsEx: { ...viewModel.lessonDetails, lessonSummary },
        studentsEx: lesson.defaultStudents.map(student => ({ id: student.id, participationStatus: "scheduled" })),
      });
    setSavingTopic(true);
    setError("");
    setValidationAttempted(false);
    saveLessonRegister(payload)
      .then((savedRegister) => {
        setDraft(currentDraft => rebuildAttendanceDraftPreservingDecisions(currentDraft, {
          ...lesson,
          lessonRegister: savedRegister,
        }));
        setSavedTopic(lessonSummary);
        setTopicSaved(true);
        setDraftKey([
          classId, lessonIndex, savedRegister.id || "",
          (savedRegister.lessonDetails || {}).attendanceStatus || "",
          (savedRegister.lessonDetails || {}).lessonStatus || "",
        ].join(":"));
      })
      .catch(saveError => setError(interfaceError(saveError, 'manage:couldNotSaveTheTopic_50')))
      .finally(() => setSavingTopic(false));
  };

  const loadLessonHistory = (cursor, append = false) => {
    setHistoryModal(current => ({ ...current, open: true, loading: true, error: "" }));
    LessonRegisterService.getClassLessonSummaryHistory(viewModel.scheduledLesson.id, { limit: 10, cursor })
      .then((result) => {
        setHistoryModal(current => ({
          ...current,
          loading: false,
          items: append ? [...current.items, ...(result.items || [])] : (result.items || []),
          nextCursor: result.nextCursor || null,
        }));
      })
      .catch((historyError) => {
        setHistoryModal(current => ({
          ...current,
          loading: false,
          error: interfaceError(historyError, 'manage:couldNotLoadPreviousTopics_51'),
        }));
      });
  };

  const handleSave = () => {
    const nextValidation = validateAttendanceDraft(draft);
    if (!nextValidation.isValid) {
      setValidationAttempted(true);
      setError(interfaceMessage('manage:attendanceCheck', { count: nextValidation.unmarkedCount }));
      return;
    }

    const payload = buildAttendanceSavePayload(draft, userRole, userTeacherId);
    if (!payload) {
      setValidationAttempted(false);
      setError(tr("manage:youDoNotHavePermissionToSaveAttendance_52"));
      return;
    }

    setSaving(true);
    setError("");
    setValidationAttempted(false);
    saveLessonRegister(payload)
      .then(() => {
        const restoredDashboardState = readDashboardState(dashboardScope);

        writeDashboardState(dashboardScope, {
          teacherId: viewModel.lessonDetails.teacherId,
          date: (restoredDashboardState && restoredDashboardState.date) ||
            getLessonDashboardDate(viewModel.lessonDetails.date),
        });
        setSaving(false);
        setDirty(false);
        setSavedTopic(draft.lessonDetails.lessonSummary || "");
        setValidationAttempted(false);
        if (unblockNavigationRef.current) {
          unblockNavigationRef.current();
          unblockNavigationRef.current = null;
        }
        history.goBack();
      })
      .catch((saveError) => {
        setSaving(false);
        setValidationAttempted(false);
        setError(interfaceError(saveError, 'manage:couldNotSaveAttendance_53'));
      });
  };

  const getCommandStudents = () => draft.participants.map((participant) => {
    const decisionEntry = draft.decisions[participant.id] || {};
    const { decision } = decisionEntry;
    const cancelled = decision === "cancelledOnTime" || decision === "cancelledLate";

    return {
      id: participant.id,
      isPresent: decision === "present",
      ...(cancelled ? { participationStatus: decision } : {}),
      ...(decisionEntry.cancellation ? { cancellation: decisionEntry.cancellation } : {}),
    };
  });

  const getPreservedCommandStudents = () => (
    viewModel.scheduledLesson.lessonRegister
      ? viewModel.scheduledLesson.lessonRegister.students || []
      : getCommandStudents()
  );

  const ensureLessonRegisterForCommand = (lessonDetails) => {
    if (viewModel.scheduledLesson.lessonRegister) {
      return Promise.resolve(viewModel.scheduledLesson.lessonRegister);
    }

    return Promise.resolve({
      id: viewModel.scheduledLesson.id,
      lessonId: viewModel.scheduledLesson.id,
      lessonDetails,
      students: getCommandStudents(),
    });
  };

  const handleUpdateLessonDetails = (lessonDetails) => {
    const nextLessonDetails = {
      ...viewModel.lessonDetails,
      ...lessonDetails,
    };
    const commandPayload = buildLessonRegisterCommandBasePayload({
      lesson: viewModel.scheduledLesson,
      lessonDetailsEx: nextLessonDetails,
      studentsEx: getPreservedCommandStudents(),
    });

    if (!canEditLessonDetails) {
      return saveLessonRegister({
        ...commandPayload,
        lessonDetails: {
          comment: nextLessonDetails.comment || "",
        },
      });
    }

    return saveLessonRegister(commandPayload);
  };

  const handleReportAbsence = command => (
    ensureLessonRegisterForCommand(viewModel.lessonDetails)
      .then(savedRegister => reportLessonAbsence({
        lessonId: savedRegister.id,
        ...command,
      }))
  );

  const handleCancelWholeLesson = command => (
    ensureLessonRegisterForCommand(viewModel.lessonDetails)
      .then(savedRegister => cancelWholeLesson({
        lessonId: savedRegister.id,
        ...command,
      }))
  );

  const handleUndoWholeLessonCancellation = () => undoWholeLessonCancellation({
    lessonId: viewModel.scheduledLesson.lessonRegister.id,
  });

  const handleAddStudentToClass = async studentId => {
    if (!canManageStudents) throw new Error(tr('manage:youDoNotHavePermissionToEditTheStudent_25'));
    const type = getLessonTypeById(lessonTypes, viewModel.schoolClass.classTypeId);
    const count = new Set(classStudents.filter(item => item.schoolClassId === classId).map(item => item.studentId)).size + 1;
    if (type.maxStudents && count > type.maxStudents && !await confirm(interfaceMessage('manage:classCapacityConfirmation', {
      name: type.name, count, max: type.maxStudents,
    }))) return;
    await addClassStudent({ schoolClassId: classId, studentId });
  };

  const handleRemoveStudentFromClass = (studentId) => {
    if (!canManageStudents) return Promise.reject(new Error(tr('manage:youDoNotHavePermissionToEditTheStudent_25')));
    const classStudent = classStudents.find(item => (
      item.schoolClassId === classId && item.studentId === studentId
    ));

    if (!classStudent) {
      return Promise.resolve();
    }

    return removeClassStudent(classStudent);
  };

  return (
    <div className="attendance-page">
      {confirmation}
      <div className="attendance-page__topbar">
        <button type="button" className="btn manage-button manage-teal-button" onClick={handleReturn}>
          <span aria-hidden="true" className="attendance-page__return-icon">←</span>{tr("common:back")}</button>
      </div>
      <AttendanceLessonHeader
        canUndoWholeLessonCancellation={canUndoCancellation}
        viewModel={viewModel}
        onAction={setActiveAction}
      />
      {viewModel.lessonDetails.lessonStatus === "cancelled" && (
        <div className="alert alert-secondary" role="status">{tr("manage:lessonCancelled_54")}</div>
      )}
      {error && <div className="alert alert-danger attendance-page__validation-alert" role="alert"><LocalizedMessage isError value={error} /></div>}
      {viewModel.lessonDetails.lessonStatus !== "cancelled" && (
        <LessonSummaryPanel
          value={draft.lessonDetails.lessonSummary || ""}
          canEdit={canEditLessonComment && !saving && !savingTopic}
          saveDisabled={!topicDirty || saving || savingTopic || !canEditLessonComment}
          saving={savingTopic}
          saved={topicSaved}
          onSave={handleSaveTopic}
          onChange={handleLessonSummaryChange}
          onShowHistory={() => loadLessonHistory(null)}
        />
      )}
      <AttendanceSummaryBar summary={summary} canEdit={canEdit && !savingTopic} onMarkAllPresent={handleMarkAllPresent} />
      <AttendanceDecisionList
        participants={draft.participants}
        decisions={draft.decisions}
        invalidStudentIds={invalidStudentIds}
        lessonDetails={viewModel.lessonDetails}
        canEdit={canEdit && !savingTopic}
        onDecisionChange={handleDecisionChange}
      />
      <AttendanceSaveBar
        canEdit={canEdit && !savingTopic}
        dirty={dirty}
        validation={validation}
        validationAttempted={validationAttempted}
        saving={saving}
        onSave={handleSave}
        onReturn={handleReturn}
      />
      {pendingNavigation && (
        <div className="attendance-page__modal-placeholder" role="dialog" aria-modal="true" aria-labelledby="attendance-leave-title">
          <div className="attendance-page__modal-card">
            <h3 id="attendance-leave-title" className="attendance-page__modal-title">{tr("manage:discardUnsavedChanges_55")}</h3>
            <p className="attendance-page__modal-text">{tr("manage:youHaveUnsavedChangesToTheTopicOrAttendance_56")}</p>
            <div className="attendance-page__modal-footer">
              <button type="button" className="btn manage-button manage-teal-button" onClick={cancelNavigation}>{tr("manage:stayOnPage_57")}</button>
              <button type="button" className="btn manage-button manage-red-button" onClick={confirmNavigation}>{tr("manage:discardChanges_58")}</button>
            </div>
          </div>
        </div>
      )}
      {pendingCancelledPresence && (
        <div
          className="attendance-page__modal-placeholder"
          role="dialog"
          aria-modal="true"
          aria-labelledby="attendance-cancelled-presence-title"
        >
          <div className="attendance-page__modal-card">
            <h3 id="attendance-cancelled-presence-title" className="attendance-page__modal-title">{tr("manage:confirmAttendanceForAStudentWhoCancelled_59")}</h3>
            <p className="attendance-page__modal-text">
              {pendingCancelledPresence.studentName || tr("manage:student_14")} {tr("manage:cancelledTheLessonAreYouSureYouWantTo_60")}</p>
            <div className="attendance-page__modal-footer">
              <button type="button" className="btn manage-button manage-teal-button" onClick={cancelCancelledPresence}>{tr("manage:keepCancellation_61")}</button>
              <button type="button" className="btn manage-button manage-red-button" onClick={confirmCancelledPresence}>{tr("manage:confirmAttendance_62")}</button>
            </div>
          </div>
        </div>
      )}
      <AttendanceCommandModals
        lessonTypes={lessonTypes}
        activeAction={activeAction}
        viewModel={viewModel}
        teachers={teachers}
        canEditLessonComment={canEditLessonComment}
        canEditLessonDetails={canEditLessonDetails}
        canManageStudents={canManageStudents}
        serverStudentSearch={userRole === ROLES.TEACHER}
        allStudents={students}
        classStudents={classStudents}
        canUseAdministrativeCommands={canUseAdministrativeCommands}
        administrativeCommandBlockReason={administrativeCommandBlockReason}
        canUndoWholeLessonCancellation={canUndoCancellation}
        onClose={() => setActiveAction("")}
        onUpdateLessonDetails={handleUpdateLessonDetails}
        onAddStudent={handleAddStudentToClass}
        onRemoveStudent={handleRemoveStudentFromClass}
        onReportAbsence={handleReportAbsence}
        onCancelWholeLesson={handleCancelWholeLesson}
        onUndoWholeLessonCancellation={handleUndoWholeLessonCancellation}
      />
      <ClassLessonHistoryModal
        open={historyModal.open}
        loading={historyModal.loading}
        error={historyModal.error}
        items={historyModal.items}
        nextCursor={historyModal.nextCursor}
        onClose={() => setHistoryModal(current => ({ ...current, open: false }))}
        onLoadMore={() => loadLessonHistory(historyModal.nextCursor, true)}
      />
    </div>
  );
}

AttendancePageBase.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      classId: PropTypes.string.isRequired,
      lessonIndex: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    block: PropTypes.func,
    goBack: PropTypes.func.isRequired,
    location: PropTypes.object,
    push: PropTypes.func,
    replace: PropTypes.func,
  }).isRequired,
  schoolClasses: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  classStudents: PropTypes.array.isRequired,
  lessonRegister: PropTypes.array.isRequired,
  books: PropTypes.array.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
  userId: PropTypes.string,
  schoolId: PropTypes.string,
  activeContextKey: PropTypes.string,
  loadSchoolClasses: PropTypes.func.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  loadClassStudents: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  loadBooks: PropTypes.func.isRequired,
  saveLessonRegister: PropTypes.func.isRequired,
  reportLessonAbsence: PropTypes.func.isRequired,
  cancelWholeLesson: PropTypes.func.isRequired,
  undoWholeLessonCancellation: PropTypes.func.isRequired,
  addClassStudent: PropTypes.func.isRequired,
  removeClassStudent: PropTypes.func.isRequired,
};

AttendancePageBase.defaultProps = {
  userRole: "",
  userTeacherId: "",
  userId: "",
  schoolId: "",
  activeContextKey: "",
};

const mapStateToProps = state => ({
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  students: state.students,
  classStudents: state.classStudents,
  lessonRegister: state.lessonRegister,
  books: state.books,
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
  userRole: getEffectivePermissionRole(state.user),
  userTeacherId: state.user.teacherId,
  userId: state.user.id,
  schoolId: state.user.schoolId,
  activeContextKey: state.user.activeContextKey,
});

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  loadBooks: bookActions.loadBooks,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  reportLessonAbsence: lessonRegisterActions.reportLessonAbsence,
  cancelWholeLesson: lessonRegisterActions.cancelWholeLesson,
  undoWholeLessonCancellation: lessonRegisterActions.undoWholeLessonCancellation,
  addClassStudent: classStudentsActions.addClassStudent,
  removeClassStudent: classStudentsActions.removeClassStudent,
};

export default connect(mapStateToProps, mapDispatchToProps)(AttendancePageBase);
